// Menecer təsdiqi dialoqu.
//
// İstifadə (useConfirm / useKitchenPicker ilə eyni naxış):
//
//   const { askApproval, approvalDialog } = useManagerApproval();
//   const token = await askApproval({
//     scope: APPROVAL_SCOPES.voidItemAfterKitchen,
//     context: { orderId, itemId },
//     title: "Sətri silmək üçün menecer təsdiqi",
//   });
//   if (!token) return;                    // ləğv edildi
//   ...delete({ approval_token: token })
//
// Ofisant ÖZ hesabında qalır — menecer yalnız PIN yazır, sessiya açılmır.
// Səbəb və təhlükəsizlik detalları: api/pos/managerApproval.ts.

import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ShieldCheck, X, Delete } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import {
  ApprovalError,
  requestManagerApproval,
  type ApprovalScope,
} from "../api/pos/managerApproval";
import { fetchStaffRoster, type StaffEntry } from "../api/pos/deviceSession";

export interface ApprovalAsk {
  scope: ApprovalScope;
  context: Record<string, unknown>;
  /** Başlıq — nə üçün təsdiq istənildiyini yazın. */
  title: string;
  /** Əlavə izah (məs. silinəcək məhsulun adı). */
  detail?: string;
}

type Resolver = (token: string | null) => void;

/** Təsdiq verə bilən rollar. Server də eyni qaydanı tətbiq edir. */
const APPROVER_ROLES = new Set(["manager", "admin", "owner", "super-admin"]);

export function useManagerApproval(): {
  askApproval: (ask: ApprovalAsk) => Promise<string | null>;
  approvalDialog: React.ReactNode;
} {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [ask, setAsk] = useState<ApprovalAsk | null>(null);
  const [managers, setManagers] = useState<StaffEntry[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resolverRef = useRef<Resolver | null>(null);

  // Menecer siyahısı bir dəfə yüklənir. Oflayn da işləyir — roster
  // pos_local-un sinxron nüsxəsindən gəlir.
  useEffect(() => {
    let alive = true;
    fetchStaffRoster()
      .then((list) => {
        if (!alive) return;
        const approvers = (list || []).filter((u) =>
          APPROVER_ROLES.has(String(u.role || "").toLowerCase()),
        );
        setManagers(approvers);
      })
      .catch(() => setManagers([]));
    return () => {
      alive = false;
    };
  }, []);

  const finish = useCallback((token: string | null) => {
    setOpen(false);
    setPin("");
    setError(null);
    setBusy(false);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(token);
  }, []);

  const askApproval = useCallback((next: ApprovalAsk) => {
    setAsk(next);
    setPin("");
    setError(null);
    setOpen(true);
    return new Promise<string | null>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const submit = useCallback(async () => {
    if (!ask || busy) return;
    const manager = managers.find((m) => String(m.id) === selected);
    if (!manager) {
      setError(t("approval.pickManager"));
      return;
    }
    if (pin.length < 4) {
      setError(t("approval.pinTooShort"));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const grant = await requestManagerApproval({
        managerName: manager.name,
        managerPin: pin,
        scope: ask.scope,
        context: ask.context,
      });
      finish(grant.token);
    } catch (e) {
      // Xəta kodları ayrılır: "menecer tapılmadı" ilə "PIN səhvdir" fərqli
      // şeylərdir və istifadəçi hansı olduğunu bilməlidir.
      const code = e instanceof ApprovalError ? e.code : "APPROVAL_FAILED";
      const message =
        code === "USER_LOCKED"
          ? t("approval.locked")
          : code === "NOT_A_MANAGER"
            ? t("approval.notManager")
            : code === "MANAGER_NOT_FOUND"
              ? t("approval.managerNotFound")
              : t("approval.wrongPin");
      setError(message);
      setPin("");
      setBusy(false);
    }
  }, [ask, busy, finish, managers, pin, selected, t]);

  const pad = (digit: string) => {
    if (busy) return;
    setError(null);
    // PIN 6 rəqəmdən uzun ola bilməz (admin paneldəki qayda ilə eyni).
    setPin((prev) => (prev.length >= 6 ? prev : prev + digit));
  };

  const approvalDialog = (
    <AnimatePresence>
      {open && ask && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => !busy && finish(null)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 12 }}
            className="relative w-full max-w-md bg-white dark:bg-[#111318] rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="flex items-start gap-3 p-5 border-b border-gray-100 dark:border-white/[0.06]">
              <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-500/15 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-black text-gray-900 dark:text-white leading-snug">
                  {ask.title}
                </h3>
                {ask.detail && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {ask.detail}
                  </p>
                )}
              </div>
              <button
                onClick={() => !busy && finish(null)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-400"
                aria-label={t("common.close")}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {managers.length === 0 ? (
                <p className="text-sm text-center text-gray-500 dark:text-gray-400 py-4">
                  {t("approval.noManagers")}
                </p>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
                      {t("approval.manager")}
                    </label>
                    <select
                      value={selected}
                      onChange={(e) => {
                        setSelected(e.target.value);
                        setError(null);
                      }}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-[#0f1115] text-gray-900 dark:text-white font-medium"
                    >
                      <option value="">{t("approval.pickManager")}</option>
                      {managers.map((m) => (
                        <option key={String(m.id)} value={String(m.id)}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
                      {t("approval.pin")}
                    </label>
                    <div className="flex justify-center gap-2 mb-3">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <span
                          key={i}
                          className={`w-3 h-3 rounded-full transition-colors ${
                            i < pin.length
                              ? "bg-[var(--primary)]"
                              : "bg-gray-200 dark:bg-white/10"
                          }`}
                        />
                      ))}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
                        <button
                          key={d}
                          onClick={() => pad(d)}
                          className="py-4 rounded-xl bg-gray-100 dark:bg-white/[0.06] text-xl font-black text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-white/[0.1] active:scale-95 transition"
                        >
                          {d}
                        </button>
                      ))}
                      <button
                        onClick={() => setPin("")}
                        className="py-4 rounded-xl bg-gray-100 dark:bg-white/[0.06] text-sm font-bold text-gray-500 dark:text-gray-400"
                      >
                        {t("approval.clear")}
                      </button>
                      <button
                        onClick={() => pad("0")}
                        className="py-4 rounded-xl bg-gray-100 dark:bg-white/[0.06] text-xl font-black text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-white/[0.1] active:scale-95 transition"
                      >
                        0
                      </button>
                      <button
                        onClick={() => setPin((p) => p.slice(0, -1))}
                        className="py-4 rounded-xl bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center text-gray-500 dark:text-gray-400"
                        aria-label={t("approval.backspace")}
                      >
                        <Delete className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </>
              )}

              {error && (
                <p className="text-sm font-semibold text-red-600 dark:text-red-400 text-center">
                  {error}
                </p>
              )}

              <div className="grid grid-cols-2 gap-2 pt-1">
                <button
                  onClick={() => finish(null)}
                  disabled={busy}
                  className="py-3 rounded-xl font-bold bg-gray-100 dark:bg-white/[0.06] text-gray-700 dark:text-gray-300 disabled:opacity-50"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={submit}
                  disabled={busy || managers.length === 0}
                  className="py-3 rounded-xl font-bold text-white bg-[var(--primary)] hover:bg-orange-600 transition disabled:opacity-50"
                >
                  {busy ? t("approval.checking") : t("approval.confirm")}
                </button>
              </div>

              <p className="text-[11px] text-center text-gray-400 dark:text-gray-500 leading-relaxed">
                {t("approval.footnote")}
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return { askApproval, approvalDialog };
}

export default useManagerApproval;
